import { type AxiosError } from 'axios'
import {
  useMutation,
  type UseMutationResult,
  type UseMutationOptions
} from 'react-query'

import agent from 'agent'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import useAuth from 'hooks/use-auth'
import { integrationInvocationsMutationKeys } from 'queries/integration-invocations/query-keys'
import { getEndpointWithParams } from 'queries/queries-utils'
import { type buildTeamExternalEndpointConfigurationRequest } from 'utils/team-external-integrations/team-external-integrations-util'

type CreateInvocationResponse = { invocationId: string }
type CreateInvocationPayload = ReturnType<
  typeof buildTeamExternalEndpointConfigurationRequest
> &
  Pick<StatusCheckFormValues, 'multiSelectTags'>

type UseCreateWebhookInvocationOptions = Omit<
  UseMutationOptions<
    CreateInvocationResponse,
    AxiosError,
    CreateInvocationPayload
  >,
  'mutationKey' | 'mutationFn'
>

type UseCreateWebhookInvocationReturnType = UseMutationResult<
  CreateInvocationResponse,
  AxiosError,
  CreateInvocationPayload
>

function createWebhookInvocation(
  [endpoint, params]: ReturnType<
    typeof integrationInvocationsMutationKeys.createWebhookInvocation
  >,
  signal?: AbortSignal
) {
  return async (requestBody: CreateInvocationPayload) => {
    const endpointWithParams = getEndpointWithParams(endpoint, params)
    const response = await agent.post<CreateInvocationResponse>(
      endpointWithParams,
      requestBody,
      { signal }
    )

    return response.data
  }
}

// Export function type signatures
export function useCreateWebhookInvocation(
  signal?: AbortSignal,
  options?: UseCreateWebhookInvocationOptions
): UseCreateWebhookInvocationReturnType

export function useCreateWebhookInvocation(
  options?: UseCreateWebhookInvocationOptions
): UseCreateWebhookInvocationReturnType

// Export function implementation
export function useCreateWebhookInvocation(
  ...args:
    | [AbortSignal?, UseCreateWebhookInvocationOptions?]
    | [UseCreateWebhookInvocationOptions?]
) {
  const { currentTeamId: teamId } = useAuth()
  const mutationKey =
    integrationInvocationsMutationKeys.createWebhookInvocation(teamId)

  const [signalOrOptions, maybeOptions] = args
  const hasSignal = signalOrOptions instanceof AbortSignal

  return useMutation({
    ...(maybeOptions ?? signalOrOptions),
    mutationFn: createWebhookInvocation(
      mutationKey,
      hasSignal ? signalOrOptions : undefined
    ),
    mutationKey
  })
}
